import { Loading } from '@/components/loading';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { useAnimatedKeyboard } from '@/hooks/use-animated-keyboard';
import { useUpload } from '@/hooks/use-upload';
import { generateAPIUrl } from '@/utils/api-url';
import { cn } from '@/utils/cn';
import { useLocalSearchParams, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { fetch as expoFetch } from 'expo/fetch';
import { hc } from 'hono/client';
import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Platform,
  Pressable,
  StyleSheet,
  View
} from 'react-native';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useDeepCompareCallback } from 'use-deep-compare';
import type { ApiPersonaImportType } from '../../api/persona-import/[...everything]+api';
import type { ApiUploadType } from '../../api/upload/[...everything]+api';

const personaImportClient = hc<ApiPersonaImportType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});

const uploadClient = hc<ApiUploadType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});

async function uploadStart(
  token: string,
  {
    id,
    fileSize,
    contentType
  }: { id: string; fileSize: number; contentType: string }
) {
  const res = await uploadClient.api.upload.avatar.$post(
    { json: { personaImportId: id, fileSize, contentType } },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  const data = await res.json();
  if (data.valid !== true) {
    throw new Error(
      typeof data.error === 'string' ? data.error : 'Failed to upload'
    );
  }

  return data;
}

async function savePersonaImport(
  token: string,
  {
    id,
    changedMessages,
    state
  }: {
    id: string;
    changedMessages: {
      id: string;
      newIdentity: 'PERSONA' | 'OTHER';
    }[];
    state: FormState;
  }
) {
  const res = await personaImportClient.api['persona-import'][':id'].$put(
    {
      param: {
        id
      },
      json: {
        changedMessages,
        name: state.choice === 'left' ? state.left : state.right,
        otherParticipantName:
          state.choice === 'left' ? state.right : state.left,
        choice: state.choice === 'left' ? 'PERSONA' : 'OTHER',
        prompt: state.prompt.trim().length ? state.prompt : undefined,
        avatar: state.avatar.trim().length ? state.avatar : undefined
      }
    },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const result = await res.json();

  if (result.valid !== true) {
    throw new Error(result.error);
  }
}

async function getPartialTranscript(
  abortSignal: AbortSignal,
  personaImportId: string,
  token: string
) {
  const res = await personaImportClient.api['persona-import'][':id'][
    'partial-transcript'
  ].$get(
    { param: { id: personaImportId } },
    {
      init: {
        signal: abortSignal
      },
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  return res.json();
}

type GetPartialTranscript = Extract<
  Awaited<ReturnType<typeof getPartialTranscript>>,
  { valid: true }
>;

type TranscriptMessage = GetPartialTranscript['partialTranscript'][0];

const STEPS = [
  'preview',
  'left',
  'right',
  'choose',
  'avatar',
  'prompt'
] as const;

type FormState = {
  left: string;
  right: string;
  choice: string;
  prompt: string;
  avatar: string;
};

export default function PersonaPreview() {
  const { authState } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const { id } = useLocalSearchParams();
  const [transcript, setTranscript] = useState<TranscriptMessage[]>([]);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [originalTranscript, setOriginalTranscript] = useState<
    TranscriptMessage[]
  >([]);
  const [currentStep, setCurrentStep] = useState<(typeof STEPS)[number]>(
    STEPS[0]
  );
  const [isSaving, setIsSaving] = useState(false);

  const [formState, setFormState] = useState<FormState>({
    left: '',
    right: '',
    choice: '',
    prompt: '',
    avatar: ''
  });
  const router = useRouter();

  const save = useDeepCompareCallback(async () => {
    setIsSaving(true);

    const changedMessages = transcript.filter((message, index) => {
      return message.identity !== originalTranscript[index].identity;
    });

    const changes = changedMessages.map((message) => {
      return {
        id: message.id,
        newIdentity: message.identity
      };
    });

    try {
      await savePersonaImport(authState.token!, {
        id: id as string,
        changedMessages: changes,
        state: formState
      });

      router.replace(`/persona-import/process/${id}`);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  }, [transcript, originalTranscript, formState]);

  const nextStep = useCallback(() => {
    const currentIndex = STEPS.indexOf(currentStep);
    if (currentIndex + 1 < STEPS.length) {
      setCurrentStep(STEPS[currentIndex + 1]);
    } else {
      save();
    }
  }, [currentStep, save]);

  const updateTranscript = useCallback(
    (
      messageId: TranscriptMessage['id'],
      newIdentity: TranscriptMessage['identity']
    ) => {
      setTranscript((prev) => {
        return prev.map((message) => {
          if (message.id === messageId) {
            return { ...message, identity: newIdentity };
          }
          return message;
        });
      });
    },
    []
  );

  const keyboard = useAnimatedKeyboard();

  const animatedStyles = useAnimatedStyle(() => ({
    transform: [{ translateY: -keyboard.height.value }]
  }));

  const bubbleStyles = useAnimatedStyle(() => ({
    paddingBottom: keyboard.height.value
  }));

  const updateFormState = useCallback((key: keyof FormState, value: string) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    if (authState.token && typeof id === 'string') {
      getPartialTranscript(abortController.signal, id, authState.token)
        .then((resp) => {
          if (resp.valid) {
            setTranscript(resp.partialTranscript);
            setOriginalTranscript(resp.partialTranscript);
            if (resp.name) {
              updateFormState('left', resp.name);
            }
            if (resp.otherParticipantName) {
              updateFormState('right', resp.otherParticipantName);
            }
          } else {
            setLoadingError(resp.error);
          }
        })
        .catch((err) => {
          setLoadingError(err.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    return () => {
      abortController.abort();
    };
  }, [authState.token, id]);

  let StepComponent;
  switch (currentStep) {
    case 'preview':
      StepComponent = PreviewStep;
      break;
    case 'left':
      StepComponent = LeftStep;
      break;
    case 'right':
      StepComponent = RightStep;
      break;
    case 'choose':
      StepComponent = ChooseStep;
      break;
    case 'prompt':
      StepComponent = PromptStep;
      break;
    case 'avatar':
      StepComponent = AvatarStep;
      break;
    default:
      StepComponent = View;
  }

  return (
    <Fragment>
      <Head>
        <title>Persona Preview - Lexikon AI</title>
      </Head>
      <SafeAreaView
        edges={['right', 'bottom', 'left']}
        style={styles.container}
      >
        <View className="flex-1">
          {transcript.length ? (
            <Animated.View
              className="flex-shrink pt-2 web:self-center web:lg:w-[50%]"
              style={bubbleStyles}
            >
              <FlatList
                data={transcript}
                renderItem={({ item }) => (
                  <ChatBubble item={item} updateTranscript={updateTranscript} />
                )}
                ListFooterComponent={() => (
                  <View className="flex-1 items-center justify-center px-5 py-4">
                    <Text className="text-center text-gray-300">
                      Looking for more messages?
                    </Text>
                    <Text className="text-center text-gray-400">
                      This is just a preview, we'll process all messages in the
                      next step.
                    </Text>
                  </View>
                )}
                keyExtractor={(item) => item.id}
              />
            </Animated.View>
          ) : null}

          <Animated.View className="flex-grow-0" style={animatedStyles}>
            <Separator />
            <View className="px-2 py-4 web:self-center web:lg:w-[50%]">
              {isLoading ? (
                <Loading />
              ) : (
                <Fragment>
                  {loadingError ? (
                    <View className="px-12 py-4">
                      <Text className="pt-4 text-center">{loadingError}</Text>
                    </View>
                  ) : (
                    <StepComponent
                      personaImportId={id as string}
                      formState={formState}
                      nextStep={nextStep}
                      updateFormState={updateFormState}
                      isSaving={isSaving}
                    />
                  )}
                </Fragment>
              )}
            </View>
          </Animated.View>
        </View>
      </SafeAreaView>
    </Fragment>
  );
}

type StepProps = {
  personaImportId: string;
  formState: FormState;
  nextStep: () => void;
  updateFormState: (key: keyof FormState, value: string) => void;
  isSaving: boolean;
};

function PreviewStep({ nextStep }: StepProps) {
  return (
    <View>
      <Text className="mb-4 text-white">
        Above is a short preview of the conversation. We've done our best to
        separate the participants, but we might have made some mistakes.{' '}
        <Text className="native:font-semibold text-white">
          Please review the conversation and make any necessary corrections by
          {Platform.OS === 'web' ? ' clicking' : ' tapping'} on the chat
          bubbles.
        </Text>
      </Text>

      <Button onPress={nextStep}>
        <Text>Continue</Text>
      </Button>
    </View>
  );
}

function LeftStep({ nextStep, formState, updateFormState }: StepProps) {
  return (
    <View>
      <Label nativeID="left">Who is on the left (in grey)?</Label>
      <View className="pt-4">
        <Input
          aria-labelledby="left"
          onSubmitEditing={nextStep}
          autoFocus
          value={formState.left}
          onChangeText={(value) => updateFormState('left', value)}
          returnKeyType="next"
        />
      </View>

      <Button
        className="mt-4"
        disabled={formState.left.trim().length === 0}
        onPress={nextStep}
      >
        <Text>Continue</Text>
      </Button>
    </View>
  );
}

function RightStep({ nextStep, formState, updateFormState }: StepProps) {
  return (
    <View>
      <Label nativeID="right">Who is on the right (in teal)?</Label>
      <Text className="pt-2 text-gray-400">
        If it's you, please enter how the other participant refers to you.
      </Text>
      <View className="pt-4">
        <Input
          aria-labelledby="right"
          onSubmitEditing={nextStep}
          submitBehavior="submit"
          autoFocus
          value={formState.right}
          onChangeText={(value) => updateFormState('right', value)}
          returnKeyType="next"
        />
      </View>

      <Button
        className="mt-4"
        disabled={formState.right.trim().length === 0}
        onPress={nextStep}
      >
        <Text>Continue</Text>
      </Button>
    </View>
  );
}

function ChooseStep({ nextStep, formState, updateFormState }: StepProps) {
  return (
    <View>
      <Label>Which participant would you like to create a Persona of?</Label>

      <Button
        className="mt-4"
        onPress={() => {
          updateFormState('choice', 'left');
          nextStep();
        }}
      >
        <Text numberOfLines={1}>{formState.left} (Left)</Text>
      </Button>

      <Button
        className="mt-4"
        onPress={() => {
          updateFormState('choice', 'right');
          nextStep();
        }}
      >
        <Text numberOfLines={1}>{formState.right} (Right)</Text>
      </Button>
    </View>
  );
}

function PromptStep({
  isSaving,
  nextStep,
  formState,
  updateFormState
}: StepProps) {
  const characterLimit = 1024;

  return (
    <View>
      <Label nativeID="prompt">
        Do you want to add any personal preferences? Any chat content to focus
        on or avoid? (Optional)
      </Label>
      <View className="pt-4">
        <Textarea
          aria-labelledby="prompt"
          onSubmitEditing={nextStep}
          autoFocus
          value={formState.prompt}
          onChangeText={(value) => {
            if (value.length <= characterLimit) {
              updateFormState('prompt', value);
            }
          }}
          returnKeyType="send"
          maxLength={characterLimit}
        />
        <View className="flex flex-row justify-end">
          <Text className="text-right text-sm text-gray-500 dark:text-gray-400">
            {formState.prompt.length}/{characterLimit}
          </Text>
        </View>
      </View>

      <Button
        className="mt-4"
        onPress={nextStep}
        disabled={isSaving || formState.prompt.length > characterLimit}
      >
        <Text>{isSaving ? 'Saving…' : 'Continue'}</Text>
      </Button>
    </View>
  );
}

function AvatarStep({
  isSaving,
  personaImportId,
  nextStep,
  formState,
  updateFormState
}: StepProps) {
  const { authState, user } = useAuth();
  const [uploadStatus, setUploadStatus] = useState<
    'idle' | 'uploading' | 'done'
  >('idle');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const userId = user?.id;

  const { getAssetInfo, startUpload, pickFile } = useUpload();

  const pickImage = useCallback(async () => {
    setUploadError(null);
    const fileUri = await pickFile('image');

    if (!fileUri) {
      return;
    }

    const fileInfo = await getAssetInfo(fileUri);

    if (fileInfo.size > 20 * 1024 * 1024 /* 20 MB */) {
      alert('File size too large, please upload a file under 20MB.');
      return;
    }

    if (!authState.token || !userId) {
      alert('You must be logged in to upload an avatar.');
      return;
    }

    if (!fileInfo.type.startsWith('image/')) {
      alert('Invalid file type. Please upload an image file.');
      return;
    }

    setUploadStatus('uploading');

    try {
      const uploadStartResult = await uploadStart(authState.token, {
        id: personaImportId,
        fileSize: fileInfo.size,
        contentType: fileInfo.type
      });

      startUpload({
        fileUri,
        mimeType: fileInfo.type,
        presignedUrl: uploadStartResult.presignedUrl,
        onSuccess: () => {
          updateFormState('avatar', uploadStartResult.uploadId);
          setUploadStatus('done');
          setUploadError(null);
        },
        onError: (error) => {
          console.error(error);
          setUploadStatus('idle');
          setUploadError('Failed to upload avatar');
        }
      });
    } catch (error: any) {
      console.log(error);
      setUploadStatus('idle');
      setUploadError(error.toString());
      return;
    }
  }, [
    authState.token,
    userId,
    personaImportId,
    getAssetInfo,
    startUpload,
    pickFile
  ]);

  return (
    <View>
      <Label>Do you want to upload an avatar?</Label>

      {uploadError && <Text className="text-red-500">{uploadError}</Text>}

      <Button
        variant={formState.avatar ? 'outline' : 'default'}
        className="mt-4"
        onPress={pickImage}
        disabled={uploadStatus === 'uploading'}
      >
        <Text>
          {uploadStatus === 'uploading'
            ? 'Uploading…'
            : uploadStatus === 'done'
              ? 'Uploaded!'
              : 'Select Avatar'}
        </Text>
      </Button>

      <Button
        variant={formState.avatar ? 'default' : 'outline'}
        className="mt-4"
        disabled={isSaving}
        onPress={nextStep}
      >
        {isSaving ? (
          <View className="py-1">
            <ActivityIndicator color="black" />
          </View>
        ) : (
          <Text>
            {formState.avatar ? 'Continue' : 'Continue without Avatar'}
          </Text>
        )}
      </Button>
    </View>
  );
}

function ChatBubble({
  item,
  updateTranscript
}: {
  item: TranscriptMessage;
  updateTranscript: (
    messageId: TranscriptMessage['id'],
    newIdentity: TranscriptMessage['identity']
  ) => void;
}) {
  const isMe = item.identity === 'OTHER';

  return (
    <View
      className={cn(
        'justify-center px-2 pb-3.5',
        'pb-1',
        isMe ? 'items-end pl-16' : 'items-start pr-16'
      )}
    >
      <Animated.View>
        <View>
          <View
            className={cn(
              'absolute bottom-0 items-center justify-center',
              isMe ? '-right-2.5' : '-left-2'
            )}
          >
            {/* little conversation bubble 'tail' only for iOS? */}
            {Platform.OS === 'ios' && (
              <>
                <View
                  className={cn(
                    'h-5 w-5 rounded-full',
                    isMe ? 'bg-primary' : 'bg-muted'
                  )}
                />
                {/* These next two views are hidden circles designed to cover up parts of the previous circle */}
                <View
                  className={cn(
                    'absolute h-5 w-5 rounded-full bg-background',
                    isMe ? '-right-2' : 'right-2'
                  )}
                />
                <View
                  className={cn(
                    'absolute h-5 w-5 -translate-y-1 rounded-full bg-background',
                    isMe ? '-right-2' : 'right-2'
                  )}
                />
              </>
            )}
          </View>
          <View>
            <Pressable
              onPress={() =>
                updateTranscript(item.id, isMe ? 'PERSONA' : 'OTHER')
              }
            >
              <View
                style={styles.borderCurve}
                className={cn(
                  'rounded-2xl bg-muted px-3 py-1.5 dark:bg-muted-foreground',
                  Platform.OS === 'ios' && 'dark:bg-muted',
                  isMe && 'bg-primary dark:bg-primary'
                )}
              >
                <Text className={cn(isMe ? 'text-black' : 'text-white')}>
                  {item.content}
                </Text>
              </View>
            </Pressable>
          </View>
        </View>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  borderCurve: {
    borderCurve: 'continuous'
  },
  container: {
    flex: 1
  }
});
